import { DOCUMENT } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewRef,
    inject
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HashStorageService } from '@df/browser/storage/hash-storage.service';
import { App } from '@df/core/app';
import { getAbsolutePosition } from '@df/dom/fn/get-absolute-position';
import { isInViewport } from '@df/dom/fn/is-in-viewport';
import { ScrollToService } from '@df/dom/scroll/scroll-to.service';
import { TimeoutService } from '@df/ng/timeout.service';
import each from 'lodash-es/each';
import findIndex from 'lodash-es/findIndex';
import first from 'lodash-es/first';
import sortBy from 'lodash-es/sortBy';
import { NFormWizard } from './form-wizard.interface';
let _formWizardUid = 0;
export const formWizardUid = () => {
    return _formWizardUid++;
};
@Component({
    selector: 'form-wizard',
    templateUrl: './form-wizard.component.html',
    standalone: true,
    imports: [FormsModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormWizardComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input()
    scrollContainer?: HTMLElement | string;
    @Output()
    stepChange = new EventEmitter<void>();
    steps: NFormWizard.ISteps = {};
    /**
     * Ordered steps sequence
     */
    stepsSequence: NFormWizard.IStep[] = [];
    /**
     * Ordered steps sequence (visible only)
     */
    visibleStepsSequence: NFormWizard.IStep[] = [];
    private _initiated = false;
    /**
     * Just inform about component being destroyed for async things
     */
    private _destroyed = false;
    private _lastStepName?: string;
    /**
     * Currently visible step
     */
    protected set currentStepName(currentStepName: string | undefined) {
        this._lastStepName = this.currentStepName;
        this.hashStorageService.set('step', currentStepName);
        this.stepChange.emit();
        this.detectChanges();
    }
    protected get currentStepName(): string | undefined {
        return this.hashStorageService.get('step');
    }

    private scrollToService = inject(ScrollToService);

    constructor(
        protected elementRef: ElementRef,
        readonly app: App,
        protected changeDetectorRef: ChangeDetectorRef,
        protected timeoutService: TimeoutService,
        protected hashStorageService: HashStorageService,
        @Inject(DOCUMENT)
        protected documentRef: Document
    ) {}
    ngOnInit(): void {
        this._initiated = true;
    }
    ngAfterViewInit(): void {
        this.setInitStep();
    }
    ngOnDestroy(): void {
        this._destroyed = true;
        this.currentStepName = undefined;
    }
    registerStep(step: NFormWizard.IStep) {
        if (!this.steps[step.id]) {
            this.steps[step.id] = step;
            this.onStepsChange();
        }
    }
    unregisterStep(step: NFormWizard.IStep) {
        if (this.steps[step.id]) {
            delete this.steps[step.id];
            this.onStepsChange();
        }
    }
    get currentStep() {
        return (this.currentStepName && this.steps[this.currentStepName]) || first(this.stepsSequence);
    }
    get prevStep() {
        return this.currentStep ? this.getPrevStep(this.currentStep) : undefined;
    }
    get nextStep() {
        return this.currentStep ? this.getNextStep(this.currentStep) : undefined;
    }
    get currentStepIndex(): number {
        return this.currentStep ? this.getStepIndex(this.currentStep) : -1;
    }
    getStepIndex(_step: NFormWizard.IStep | string): number {
        const step = _step ? this.toStep(_step) : undefined;
        if (!step) {
            return -1;
        }
        return findIndex(this.stepsSequence, i => i === step);
    }
    getPrevStep(vsStep: NFormWizard.IStep | string): NFormWizard.IStep | undefined {
        const order = this.toStep(vsStep)?.order || 0;
        const steps = [...this.stepsSequence].reverse();
        return steps.find(i => i.order < order) || first(this.stepsSequence);
    }
    goToPrevStep(vsStep?: NFormWizard.IStep | string) {
        const prevStep = vsStep ? this.getPrevStep(vsStep) : this.prevStep;
        if (prevStep) {
            this.goToStep(prevStep, false);
        }
    }
    getNextStep(vsStep: NFormWizard.IStep | string): NFormWizard.IStep | undefined {
        const order = this.toStep(vsStep)?.order || 0;
        return this.stepsSequence.find(i => i.order > order);
    }
    goToNextStep(vsStep?: NFormWizard.IStep | string) {
        const nextStep = vsStep ? this.getNextStep(vsStep) : this.nextStep;
        if (nextStep) {
            this.goToStep(nextStep);
        }
    }
    getStepByName(name: string): NFormWizard.IStep | undefined {
        return this.steps[name];
    }
    toStep(step: string | NFormWizard.IStep): NFormWizard.IStep | undefined {
        return typeof step === 'string' ? this.getStepByName(step) : step;
    }
    goToStep(step?: string | NFormWizard.IStep, check = true): void {
        step = step ? this.toStep(step) : undefined;
        if (!step) {
            return;
        }
        each(this.steps, i => {
            if (i !== step) {
                i.markUnsubmitted();
            }
        });
        if (step) {
            if (this.checkStep(<NFormWizard.IStep>step, true) && check) {
                this.currentStepName = (<NFormWizard.IStep>step).id;
            } else if (!check) {
                this.currentStepName = (<NFormWizard.IStep>step).id;
            }
            // make sure step is properly visible when changing
            this.timeoutService.setTimeout(() => {
                const currentStep = this.currentStep;
                if (currentStep) {
                    const stepElement = currentStep.domElement;
                    if (stepElement && this.isScrollToElementRequired(stepElement)) {
                        const top = Math.max(0, getAbsolutePosition(stepElement as HTMLElement).y - 20);
                        this.scrollToTop(top);
                    }
                }
            }, 10); // give it a moment to appear
        } else {
            this.app.logger.error('Trying to go to not existing step');
        }
    }
    /**
     * Checks all steps before given one
     * If true value passed as `step` will check all steps
     */
    checkStep(step: NFormWizard.IStep | boolean | undefined = this.currentStep, updateForms = false): boolean {
        if (step) {
            let firstInvalid: NFormWizard.IStep | undefined;
            if (step === true) {
                firstInvalid = this.stepsSequence.find(i => i && !i.valid);
            } else {
                firstInvalid = this.stepsSequence.find(i => i && i.order < (<NFormWizard.IStep>step).order && !i.valid);
            }
            if (firstInvalid) {
                this.onCheckStepFailure(firstInvalid, updateForms);
                return false;
            }
        }
        return true;
    }
    /**
     * Called when steps change
     */
    onStepsChange() {
        // there were some problems with debounce here, but it seems to be fine now
        if (!this._destroyed) {
            this.stepsSequence = sortBy(Object.values(this.steps), 'order');
            this.visibleStepsSequence = this.stepsSequence.filter(i => !i.hidden);
            this.detectChanges();
        }
    }
    /**
     * Tracking function used with checkout steps
     */
    stepTrackFn(index: number, step: NFormWizard.IStep): string {
        return step.id;
    }
    scrollToTop(top = 0): void {
        const scrollContainer = this.scrollContainer;
        if (scrollContainer) {
            if (typeof scrollContainer === 'string') {
                this.documentRef.querySelector(scrollContainer)?.scrollTo({
                    top
                });
            } else {
                (scrollContainer as HTMLElement)?.scrollTo({
                    top
                });
            }
        } else {
            this.scrollToService.scrollTo(top);
        }
    }
    protected onCheckStepFailure(firstInvalid: NFormWizard.IStep, updateForms: boolean): void {
        this.currentStepName = firstInvalid.id;
        if (updateForms) {
            firstInvalid.markSubmitted();
        }
    }
    protected setInitStep(): void {
        if (!this.currentStepName) {
            this.currentStepName = this.currentStep?.id;
        }
        this.scrollToTop();
    }
    protected isScrollToElementRequired(element: HTMLElement): boolean {
        // if top part of step is not in viewport scroll to it
        // when on SM sizes and stepElement is below 60% of viewport height scroll to it anyway
        return !isInViewport(element, 0, 'top') || (this.app.client.sizeId < 2 && !isInViewport(element, 0.6, 'y'));
    }
    protected detectChanges() {
        if (!(<ViewRef>this.changeDetectorRef).destroyed && this._initiated) {
            this.changeDetectorRef.detectChanges();
        }
    }
}
